import {DOCUMENT} from '@angular/common';
import {Inject, Injectable} from '@angular/core';

export type AtlasTheme = 'light' | 'dark';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private readonly storageKey: string = 'atlas.theme';
  private currentTheme: AtlasTheme = 'light';

  constructor(@Inject(DOCUMENT) private document: Document) {}

  initialize(): AtlasTheme {
    const storedTheme: string = this.readStoredTheme();
    const theme: AtlasTheme = this.isTheme(storedTheme) ? storedTheme : this.getSystemTheme();

    this.applyTheme(theme);
    return theme;
  }

  toggle(): AtlasTheme {
    return this.setTheme(this.currentTheme === 'dark' ? 'light' : 'dark');
  }

  setTheme(theme: AtlasTheme): AtlasTheme {
    this.applyTheme(theme);
    this.storeTheme(theme);
    return theme;
  }

  applyForCurrentView(theme: AtlasTheme): AtlasTheme {
    this.applyTheme(theme);
    return theme;
  }

  private applyTheme(theme: AtlasTheme): void {
    this.currentTheme = theme;
    this.document.documentElement.setAttribute('data-atlas-theme', theme);
    this.document.documentElement.style.setProperty('color-scheme', theme);

    const themeColor: HTMLMetaElement = this.document.querySelector('meta[name="theme-color"]');
    if (themeColor) {
      themeColor.setAttribute('content', theme === 'dark' ? '#0b1120' : '#0f172a');
    }
  }

  private getSystemTheme(): AtlasTheme {
    const defaultView: Window = this.document.defaultView;
    if (defaultView && defaultView.matchMedia && defaultView.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }

    return 'light';
  }

  private readStoredTheme(): string {
    try {
      const defaultView: Window = this.document.defaultView;
      return defaultView && defaultView.localStorage ? defaultView.localStorage.getItem(this.storageKey) : null;
    } catch (error) {
      return null;
    }
  }

  private storeTheme(theme: AtlasTheme): void {
    try {
      const defaultView: Window = this.document.defaultView;
      if (defaultView && defaultView.localStorage) {
        defaultView.localStorage.setItem(this.storageKey, theme);
      }
    } catch (error) {}
  }

  private isTheme(theme: string): theme is AtlasTheme {
    return theme === 'light' || theme === 'dark';
  }
}
